// Sound engine and the game-facing sound API.
//
// Mix: sfx + music (duckable) → stage reverb send → "hurt" low-pass → bus
// compressor → master (mute). Instruments and effects are synthesized once
// into buffers (./synth) at the first user gesture, then played back with
// per-hit variation in pitch, level and stereo position.

import type { StageId } from '@/render/stages';
import type { Side } from '@/sim/sim';
import { TUNING as T, type EnemyKind } from '@/sim/tuning';
import * as S from './synth';
import { Music } from './music';

/**
 * How a hit on each opponent sounds, following their build: big ones lower
 * with a deep thump underneath, lean ones lighter and higher, the gloved
 * blocker with a pad of glove in it.
 */
const TARGET: Record<EnemyKind, { rate: number; gain: number; thump: number; glove: number }> = {
  basic: { rate: 1, gain: 1, thump: 0, glove: 0 },
  heavy: { rate: 0.82, gain: 1.1, thump: 0.45, glove: 0 },
  grappler: { rate: 0.87, gain: 1.05, thump: 0.35, glove: 0 },
  blocker: { rate: 0.97, gain: 1, thump: 0, glove: 0.35 },
  kicker: { rate: 1.06, gain: 0.92, thump: 0, glove: 0 },
  feinter: { rate: 1.1, gain: 0.88, thump: 0, glove: 0 },
};

const clamp = (x: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, x));

/** Combo counts at which grunts step up a harshness level. */
const GRUNT_COMBO = [10, 25] as const;

/**
 * Each opponent's own voice, fixed by their id: a pitch of their own (±15%) and three
 * of the grunts, so two opponents of the same kind never sound alike.
 */
function voiceOf(id: number): { rate: number; grunts: S.GruntName[] } {
  let h = Math.imul(id ^ 0x9e3779b9, 0x85ebca6b);
  h = (Math.imul(h ^ (h >>> 13), 0xc2b2ae35) ^ (h >>> 16)) >>> 0;
  const all = Object.keys(S.GRUNTS) as S.GruntName[];
  // Three distinct grunts: start anywhere, stride by a step coprime to 6
  const first = h % all.length;
  const step = [1, 5][(h >>> 8) & 1]!;
  const grunts = [0, 1, 2].map((i) => all[(first + i * step) % all.length]!);
  const rate = 0.85 + (((h >>> 16) & 0xff) / 255) * 0.3;
  return { rate, grunts };
}

const SOUND_KEY = 'kiai-sound';
/** Master level: headroom above the compressor for small phone speakers. */
const MASTER = 0.85;

export interface PlayOpts {
  gain?: number;
  rate?: number;
  pan?: number;
  at?: number;
  /** Reverb send level (0 = dry). */
  send?: number;
  music?: boolean;
}

/** The two pitched instruments are rendered per scale degree. */
export const DEGREES = 13;

export class Sfx {
  private ctx: BaseAudioContext | null = null;
  private master: GainNode | null = null;
  private sfxBus: GainNode | null = null;
  private musicBus: GainNode | null = null;
  private duck: GainNode | null = null;
  private hurtFilter: BiquadFilterNode | null = null;
  private reverb: ConvolverNode | null = null;
  private reverbIn: GainNode | null = null;
  private buffers = new Map<string, AudioBuffer>();
  private rooms = new Map<StageId, AudioBuffer>();
  private muted = false;
  readonly music = new Music(this);

  constructor() {
    try {
      this.muted = localStorage.getItem(SOUND_KEY) === 'off';
    } catch {
      this.muted = false;
    }
  }

  get isMuted(): boolean {
    return this.muted;
  }

  /** @internal for Music */
  get context(): BaseAudioContext | null {
    return this.ctx;
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    try {
      localStorage.setItem(SOUND_KEY, muted ? 'off' : 'on');
    } catch {
      // Storage blocked: the choice lasts for this visit
    }
    if (this.ctx && this.master) {
      this.master.gain.setTargetAtTime(muted ? 0 : MASTER, this.ctx.currentTime, 0.02);
    }
  }

  /**
   * Create the context and render every sound (first user gesture). Tests pass
   * an OfflineAudioContext to render the mix without speakers.
   */
  unlock(offline?: BaseAudioContext): void {
    if (this.ctx) {
      if (this.ctx instanceof AudioContext) void this.ctx.resume().catch(() => undefined);
      return;
    }
    try {
      const ctx = offline ?? new AudioContext();
      this.ctx = ctx;
      this.buildGraph(ctx);
      this.render(ctx);
      if (ctx instanceof AudioContext) this.keepAwake();
    } catch {
      this.ctx = null;
    }
  }

  /**
   * Phones suspend (or "interrupt") the sound when the browser goes to the
   * background, and it only comes back from the player's own tap: so it's
   * suspended while the page is hidden. Resuming it wasn't enough on iOS (the
   * user heard nothing after Resume, 2026-10-18: the context can say it runs
   * and stay silent), so the first tap or key once the page is back (Resume,
   * a menu button) opens a new one in that gesture, as the first was opened
   * (a pointerdown waits for it: iOS doesn't count it as a gesture).
   */
  private keepAwake(): void {
    // Only a live context (not an offline one) gets here, and reopen() keeps it one
    let asleep = false;
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) return;
      asleep = true;
      void (this.ctx as AudioContext).suspend().catch(() => undefined);
    });
    const wake = (e: Event): void => {
      const ctx = this.ctx as AudioContext;
      if (document.hidden) return;
      if (!asleep) {
        if (ctx.state !== 'running') void ctx.resume().catch(() => undefined);
      } else if (e.type !== 'pointerdown') {
        asleep = false;
        this.reopen(ctx);
      }
    };
    for (const type of ['pointerdown', 'touchend', 'keydown', 'click']) {
      window.addEventListener(type, wake, { capture: true, passive: true });
    }
  }

  /** A new context in place of `old`, at its rate: the graph built again, and
   *  every rendered sound and room fits it as it is (a convolver takes only its own rate).
   *  If the new one won't start in this event, the next tap resumes it (wake()). */
  private reopen(old: AudioContext): void {
    let ctx = old;
    try {
      ctx = new AudioContext({ sampleRate: old.sampleRate });
      this.wire(ctx);
    } catch {
      // Nothing left half-built: back to the old one, its graph built again
      if (ctx !== old) void ctx.close().catch(() => undefined);
      ctx = old;
      this.wire(old);
    }
    void ctx.resume().catch(() => undefined);
    if (ctx !== old) void old.close().catch(() => undefined);
  }

  private wire(ctx: AudioContext): void {
    this.ctx = ctx;
    this.buildGraph(ctx);
    this.setStage(this.stage);
  }

  private buildGraph(ctx: BaseAudioContext): void {
    this.master = ctx.createGain();
    this.master.gain.value = this.muted ? 0 : MASTER;
    this.master.connect(ctx.destination);
    // Bus compressor: loud moments (kiai, knockouts) stay clean
    const comp = ctx.createDynamicsCompressor();
    comp.threshold.value = -14;
    comp.knee.value = 10;
    comp.ratio.value = 4;
    comp.attack.value = 0.004;
    comp.release.value = 0.18;
    comp.connect(this.master);
    // Everything passes one low-pass that closes briefly when the player is hit
    this.hurtFilter = ctx.createBiquadFilter();
    this.hurtFilter.type = 'lowpass';
    this.hurtFilter.frequency.value = 20000;
    this.hurtFilter.Q.value = 0.7;
    this.hurtFilter.connect(comp);
    this.sfxBus = ctx.createGain();
    this.sfxBus.gain.value = 0.9;
    this.sfxBus.connect(this.hurtFilter);
    this.duck = ctx.createGain();
    this.duck.connect(this.hurtFilter);
    this.musicBus = ctx.createGain();
    this.musicBus.gain.value = 0.34;
    this.musicBus.connect(this.duck);
    this.reverb = ctx.createConvolver();
    const wet = ctx.createGain();
    wet.gain.value = 0.55;
    this.reverb.connect(wet).connect(this.hurtFilter);
    this.reverbIn = ctx.createGain();
    this.reverbIn.connect(this.reverb);
  }

  private toBuffer(ctx: BaseAudioContext, data: S.Mono | [S.Mono, S.Mono]): AudioBuffer {
    const chans = Array.isArray(data) ? data : [data];
    const b = ctx.createBuffer(chans.length, chans[0]!.length, ctx.sampleRate);
    chans.forEach((c, i) => b.getChannelData(i).set(c));
    return b;
  }

  /**
   * Render every sound. Strike and menu sounds are needed at once and render
   * immediately; drums, koto, shakuhachi and the reverb rooms follow in small
   * slices so the first tap never stalls a phone (music starts later anyway).
   */
  private render(ctx: BaseAudioContext): void {
    const sr = ctx.sampleRate;
    type Job = [string, () => S.Mono | [S.Mono, S.Mono]];
    const now: Job[] = [
      ['click', () => S.woodClick(sr)],
      ['giSnap', () => S.giSnap(sr)],
      ['whooshPunch', () => S.whoosh(sr, 0.12, 900, 3200, 7)],
      ['whooshKick', () => S.whoosh(sr, 0.22, 380, 1800, 17)],
      ['whooshMiss', () => S.whoosh(sr, 0.3, 600, 2600, 27)],
      ['impactHeavy', () => S.impact(sr, 0.85, 18)],
      ...(Object.keys(S.STRIKES) as S.StrikeName[]).map((k): Job => [
        `${k}:0`,
        () => S.strike(sr, k, 0),
      ]),
      ['glove', () => S.glove(sr)],
      ['bodyBlow', () => S.bodyBlow(sr)],
      ['windup', () => S.windup(sr)],
    ];
    const later: Job[] = [
      // The other takes of each strike: until they're in, every hit plays the first
      ...(Object.keys(S.STRIKES) as S.StrikeName[]).flatMap((k) =>
        Array.from({ length: S.STRIKE_TAKES - 1 }, (_, i): Job => [
          `${k}:${i + 1}`,
          () => S.strike(sr, k, i + 1),
        ]),
      ),
      ['bell', () => S.bell(sr, 1320)],
      ...(Object.keys(S.GRUNTS) as S.GruntName[]).flatMap((k) =>
        Array.from({ length: S.GRUNT_HARSHNESS }, (_, h): Job => [
          `grunt:${k}:${h}`,
          () => S.grunt(sr, k, h / (S.GRUNT_HARSHNESS - 1)),
        ]),
      ),
      ...(Object.keys(S.KIAIS) as S.KiaiName[]).map((k): Job => [`kiai:${k}`, () => S.kiai(sr, k)]),
      ['beep', () => S.beep(sr, 1320)],
      ['taiko', () => S.taiko(sr, 78, 0.9, 1)],
      ['taikoLow', () => S.taiko(sr, 52, 1.4, 21)],
      ['ka', () => S.ka(sr)],
      ['shime', () => S.shime(sr)],
      ...Array.from({ length: DEGREES }, (_, d): Job => [
        `koto${d}`,
        () => S.koto(sr, S.scaleFreq(d + 5)),
      ]),
      ...(Object.keys(S.ROOMS) as StageId[]).map((stage): Job => [
        `room:${stage}`,
        () => S.impulse(sr, S.ROOMS[stage]),
      ]),
      ...Array.from({ length: DEGREES }, (_, d): Job => [
        `flute${d}`,
        () => S.flute(sr, S.scaleFreq(d + 5) * 2, 1.8),
      ]),
    ];
    const run = ([name, gen]: Job): void => {
      const buf = this.toBuffer(ctx, gen());
      if (name.startsWith('room:')) {
        const stage = name.slice(5) as StageId;
        this.rooms.set(stage, buf);
        if (this.reverb && (stage === this.stage || !this.reverb.buffer)) this.reverb.buffer = buf;
      } else this.buffers.set(name, buf);
    };
    now.forEach(run);
    this.readyPromise = new Promise((resolve) => {
      const slice = (): void => {
        const until = performance.now() + 8;
        while (later.length && performance.now() < until) run(later.shift()!);
        if (later.length) setTimeout(slice, 0);
        else resolve();
      };
      setTimeout(slice, 0);
    });
  }

  private readyPromise: Promise<void> = Promise.resolve();
  private stage: StageId = 'dojo';

  /** Resolves once every sound has been rendered (offline checks wait for it). */
  ready(): Promise<void> {
    return this.readyPromise;
  }

  /** Switch the reverb to the current stage's room. */
  setStage(stage: StageId): void {
    this.stage = stage;
    const room = this.rooms.get(stage);
    if (this.reverb && room && this.reverb.buffer !== room) this.reverb.buffer = room;
    this.music.setStage(stage);
  }

  /** @internal Play a rendered buffer. */
  play(name: string, o: PlayOpts = {}): void {
    const ctx = this.ctx;
    const buf = this.buffers.get(name);
    if (!ctx || !buf || !this.sfxBus || !this.musicBus || !this.reverbIn) return;
    const at = Math.max(ctx.currentTime, o.at ?? this.now());
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.playbackRate.value = o.rate ?? 1;
    const g = ctx.createGain();
    g.gain.value = o.gain ?? 1;
    let node: AudioNode = src.connect(g);
    if (o.pan) {
      const p = ctx.createStereoPanner();
      p.pan.value = Math.max(-1, Math.min(1, o.pan));
      node = node.connect(p);
    }
    node.connect(o.music ? this.musicBus : this.sfxBus);
    if (o.send) {
      const s = ctx.createGain();
      s.gain.value = o.send;
      node.connect(s).connect(this.reverbIn);
    }
    src.start(at);
  }

  /** Offline rendering: schedule events at a chosen time instead of "now". */
  clock: number | null = null;

  private now(): number {
    return this.clock ?? this.ctx?.currentTime ?? 0;
  }

  /** Small random variation so repeated hits don't sound machine-gunned. */
  private vary(amount = 0.06): number {
    return 1 + (Math.random() * 2 - 1) * amount;
  }

  /** Duck the music (and optionally muffle everything) for a moment. */
  private dip(musicTo: number, hold: number, muffle = false): void {
    const ctx = this.ctx;
    if (!ctx || !this.duck || !this.hurtFilter) return;
    const t = Math.max(ctx.currentTime, this.now());
    this.duck.gain.cancelScheduledValues(t);
    this.duck.gain.setTargetAtTime(musicTo, t, 0.015);
    this.duck.gain.setTargetAtTime(1, t + hold, 0.25);
    if (muffle) {
      const f = this.hurtFilter.frequency;
      f.cancelScheduledValues(t);
      f.setTargetAtTime(700, t, 0.01);
      f.setTargetAtTime(20000, t + 0.25, 0.3);
    }
  }

  // ---- Game events ----

  /** A strike goes out: the technique cuts the air now; `landed()` follows as it gets there. */
  swing(o: { kick: boolean; side: Side }): void {
    this.play(o.kick ? 'whooshKick' : 'whooshPunch', {
      gain: 0.35,
      rate: this.vary(),
      pan: o.side * 0.45 * 0.8,
    });
  }

  /**
   * The strike reaches the opponent: the gi snaps as it does, an impact for
   * where it landed (a crack or slap to the head, a thud and an exhale to the
   * body) and who it lands on, panned to the opponent, and a grunt from them on
   * a perfect or counter.
   */
  landed(o: {
    judgement: 'perfect' | 'good' | 'counter';
    /** The opponent struck (their voice). */
    id: number;
    combo: number;
    kick: boolean;
    head: boolean;
    target: EnemyKind;
    side: Side;
    killed: boolean;
  }): void {
    const pan = o.side * 0.45;
    const t = this.now();
    const land: S.StrikeName = `${o.head ? 'head' : 'body'}${o.kick ? 'Kick' : 'Punch'}`;
    const who = TARGET[o.target];
    this.play('giSnap', { gain: 0.7, rate: this.vary(0.08), pan: pan * 0.6, send: 0.1 });
    this.play(this.takeOf(land), {
      gain: (o.killed ? 1 : 0.8) * who.gain,
      rate: this.vary(0.05) * (o.killed ? 0.92 : 1) * who.rate,
      pan,
      at: t + 0.012,
      send: 0.22,
    });
    if (who.thump) {
      this.play('impactHeavy', { gain: who.thump, rate: 0.75, pan, at: t + 0.012, send: 0.22 });
    }
    if (who.glove) this.play('glove', { gain: who.glove, rate: this.vary(), pan, at: t + 0.006 });
    // A knockout blow lands with extra weight underneath
    if (o.killed)
      this.play('impactHeavy', { gain: 0.5, rate: 0.9, pan, at: t + 0.012, send: 0.22 });
    if (o.judgement === 'counter') this.play('glove', { gain: 0.6, pan, send: 0.15 });
    if (o.judgement !== 'good') {
      // A hard, well-timed strike knocks a grunt out of the opponent: in
      // their own voice, harsher as the combo climbs
      const voice = voiceOf(o.id);
      const names = voice.grunts.filter((k) => k !== this.lastGrunt);
      this.lastGrunt = names[Math.floor(Math.random() * names.length)]!;
      const harsh = GRUNT_COMBO.filter((c) => o.combo >= c).length;
      this.play(`grunt:${this.lastGrunt}:${harsh}`, {
        gain: 0.5 + 0.08 * harsh,
        // Their voice and build, a little different every time, straining
        // higher with the combo; kept where it still sounds like a person
        rate: clamp(this.vary(0.07) * who.rate * voice.rate * (1 + 0.04 * harsh), 0.72, 1.25),
        pan,
        at: t + 0.035,
        send: 0.25,
      });
    }
  }

  /**
   * A PERFECT chain climbs the koto scale with each well-timed hit (from the
   * second on), quietly under the impact; a new tier rings the bell.
   */
  chain(n: number, side: Side): void {
    const t = this.now();
    this.play(`koto${Math.min(DEGREES - 1, n - 2)}`, {
      gain: 0.16,
      pan: side * 0.3,
      at: t + 0.03,
      send: 0.3,
    });
    if (n % T.chain.step === 0) this.play('bell', { gain: 0.1, at: t + 0.05, send: 0.5 });
  }

  /** A boss walks in: a shime roll into a low taiko. */
  bossIn(side: Side): void {
    const t = this.now();
    for (let i = 0; i < 5; i++) {
      this.play('shime', { gain: 0.2 + i * 0.07, pan: side * 0.4, at: t + i * 0.06 });
    }
    this.play('taikoLow', { gain: 0.9, pan: side * 0.3, at: t + 0.32, send: 0.5 });
    this.dip(0.4, 0.5);
  }

  /** A boss knocked into his next act: a taiko under his grunt. */
  bossAct(id: number, side: Side): void {
    const t = this.now();
    this.play('taiko', { gain: 0.7, pan: side * 0.4, at: t + 0.02, send: 0.4 });
    const voice = voiceOf(id);
    this.play(`grunt:${voice.grunts[0]!}:2`, {
      gain: 0.6,
      rate: clamp(voice.rate * 0.85, 0.72, 1.25),
      pan: side * 0.45,
      at: t + 0.05,
      send: 0.3,
    });
  }

  /** A knocked-down opponent hits the floor. */
  fall(side: Side): void {
    this.play('bodyBlow', { gain: 0.35, rate: 1.3, pan: side * 0.45 * 1.4, send: 0.2 });
  }

  blocked(side: Side): void {
    this.play('glove', { gain: 0.8, rate: this.vary(), pan: side * 0.45, send: 0.15 });
    this.play('ka', { gain: 0.35, rate: 0.8, pan: side * 0.45 });
  }

  whiff(side: Side): void {
    this.play('whooshMiss', { gain: 0.55, rate: this.vary(0.1), pan: side * 0.35, send: 0.12 });
  }

  /**
   * An opponent loading an attack: the audio tell, from their side. `faster`:
   * how much shorter than usual their windup is (deep in Kumite), so the swell
   * still peaks before the blow.
   */
  windup(side: Side, heavy: boolean, faster = 1): void {
    const rate = (heavy ? 0.75 : 1.1) * faster;
    this.play('windup', { gain: 0.3, rate, pan: side * 0.7, send: 0.1 });
  }

  hurt(side: Side): void {
    this.play('bodyBlow', { gain: 1, pan: side * 0.3, send: 0.25 });
    this.play('glove', { gain: 0.5, rate: 0.7, pan: side * 0.3 });
    this.dip(0.3, 0.35, true);
  }

  private lastKiai = '';
  private lastGrunt = '';
  private lastTake: Partial<Record<S.StrikeName, string>> = {};

  /** A take of a strike other than the one it played last (of those rendered so far). */
  private takeOf(land: S.StrikeName): string {
    const takes = Array.from({ length: S.STRIKE_TAKES }, (_, i) => `${land}:${i}`).filter(
      (k) => k !== this.lastTake[land] && this.buffers.has(k),
    );
    const take = takes[Math.floor(Math.random() * takes.length)] ?? `${land}:0`;
    this.lastTake[land] = take;
    return take;
  }

  kiai(): void {
    const t = this.now();
    // A different shout each time, never the same one twice in a row
    const names = Object.keys(S.KIAIS).filter((k) => k !== this.lastKiai);
    this.lastKiai = names[Math.floor(Math.random() * names.length)]!;
    this.play(`kiai:${this.lastKiai}`, { gain: 0.9, send: 0.6 });
    this.play('taikoLow', { gain: 1, at: t + 0.03, send: 0.5 });
    this.play('whooshKick', { gain: 0.5, rate: 0.6, at: t + 0.05 });
    this.dip(0.25, 0.6);
  }

  roundClear(): void {
    const t = this.now();
    this.play('shime', { gain: 0.5, at: t });
    [4, 6, 9].forEach((d, i) =>
      this.play(`koto${d}`, { gain: 0.5, at: t + 0.06 + i * 0.09, send: 0.35 }),
    );
  }

  /** Promotion: rolling shime into a big taiko under a rising koto run, a bell on top. */
  promotion(): void {
    const t = this.now();
    for (let i = 0; i < 6; i++) this.play('shime', { gain: 0.25 + i * 0.07, at: t + i * 0.07 });
    this.play('taikoLow', { gain: 1, at: t + 0.42, send: 0.5 });
    [0, 2, 4, 5, 7, 9, 12].forEach((d, i) =>
      this.play(`koto${d}`, { gain: 0.55, at: t + 0.42 + i * 0.08, send: 0.4 }),
    );
    this.play('bell', { gain: 0.4, at: t + 1.05, send: 0.6 });
  }

  gameover(): void {
    const t = this.now();
    this.play('taikoLow', { gain: 0.9, at: t, send: 0.5 });
    [7, 5, 3, 0].forEach((d, i) =>
      this.play(`koto${d}`, { gain: 0.5, rate: 0.5, at: t + 0.15 + i * 0.22, send: 0.5 }),
    );
  }

  /**
   * Fist into the pad: a deep thump by power, the senpai holding it breathing
   * out sharply as he takes it, then the LED counting up.
   */
  impact(power: number): void {
    const t = this.now();
    this.play('giSnap', { gain: 0.7 });
    this.play('impactHeavy', {
      gain: 0.6 + power * 0.4,
      rate: 1.15 - power * 0.25,
      at: t + 0.01,
      send: 0.3,
    });
    this.play('taiko', { gain: power * 0.8, at: t + 0.01, send: 0.3 });
    // A held breath let go ("hff"): his own low voice, from the pad's side,
    // forced out harder and straining a little on a strong hit
    this.play(`grunt:HUF:${power >= 0.7 ? 1 : 0}`, {
      gain: 0.3 + power * 0.25,
      rate: this.vary(0.03) * (0.9 - power * 0.06),
      pan: 0.4,
      at: t + 0.03,
      send: 0.25,
    });
    const beeps = 3 + Math.round(power * 6);
    for (let i = 0; i < beeps; i++) {
      this.play('beep', { gain: 0.3, rate: 0.7 + i * 0.07, at: t + 0.1 + i * 0.055 });
    }
    if (power >= 1) {
      this.play('bell', { gain: 0.45, at: t + 0.12 + beeps * 0.055, send: 0.5 });
      this.play('bell', { gain: 0.3, rate: 1.5, at: t + 0.2 + beeps * 0.055, send: 0.5 });
    }
  }

  /** Menu button. */
  click(): void {
    this.play('click', { gain: 0.35, rate: this.vary(0.04) });
  }
}
